import { useState, useEffect } from 'react';
import { Card, Badge, Button, Row, Col, Form, Modal } from 'react-bootstrap';
import { useApi } from '../../../contexts/ApiProvider';

export default function TripLogistics({ tripId }) {
    const api = useApi();
    const [reservations, setReservations] = useState([]);
    const [showResModal, setShowResModal] = useState(false);

    // --- NEW: Edit State ---
    const [showEditModal, setShowEditModal] = useState(false);
    const [editRes, setEditRes] = useState({
        id: null, res_type: 'Flight', provider: '', confirmation_code: '',
        start_datetime: '', end_datetime: '', start_location: '', end_location: '', notes: ''
    });

    const [newRes, setNewRes] = useState({
        res_type: 'Flight', provider: '', confirmation_code: '',
        start_datetime: '', end_datetime: '', start_location: '', end_location: '', notes: ''
    });

    useEffect(() => { fetchReservations(); }, [tripId]);

    const fetchReservations = async () => {
        const res = await api.get(`/travel/trips/${tripId}/reservations`);
        if (res.ok) setReservations(res.body.items || []);
    };

    const handleAddReservation = async (e) => {
        e.preventDefault();
        const res = await api.post(`/travel/trips/${tripId}/reservations`, newRes);
        if (res.ok) {
            setShowResModal(false);
            setNewRes({ res_type: 'Flight', provider: '', confirmation_code: '', start_datetime: '', end_datetime: '', start_location: '', end_location: '', notes: '' });
            fetchReservations();
        }
    };

    // --- NEW: Handle Update ---
    const handleUpdateReservation = async (e) => {
        e.preventDefault();
        const res = await api.put(`/travel/reservations/${editRes.id}`, editRes);
        if (res.ok) {
            setShowEditModal(false);
            fetchReservations();
        } else {
            alert("Failed to update reservation.");
        }
    };

    const handleDeleteReservation = async (resId) => {
        if (!window.confirm("Remove this booking from the itinerary?")) return;
        const res = await api.delete(`/travel/reservations/${resId}`);
        if (res.ok) fetchReservations();
    };

    // Helper to format ISO dates for the <input type="datetime-local">
    const formatForInput = (isoString) => {
        if (!isoString) return '';
        const d = new Date(isoString);
        // Adjust to local time zone string YYYY-MM-DDTHH:mm
        return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
    };

    const openEditModal = (res) => {
        setEditRes({
            id: res.id,
            res_type: res.res_type || 'Flight',
            provider: res.provider || '',
            confirmation_code: res.confirmation_code || '',
            start_datetime: formatForInput(res.start_datetime),
            end_datetime: formatForInput(res.end_datetime),
            start_location: res.start_location || '',
            end_location: res.end_location || '',
            notes: res.notes || ''
        });
        setShowEditModal(true);
    };

    const getResIcon = (type) => {
        switch(type) {
            case 'Flight': return 'bi-airplane-fill';
            case 'Hotel': case 'Airbnb': return 'bi-building';
            case 'Rental Car': return 'bi-car-front-fill';
            case 'Train': return 'bi-train-front-fill';
            default: return 'bi-ticket-perforated-fill';
        }
    };

    return (
        <div className="bg-white p-4 fade-in">
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <h5 className="fw-bold mb-0">Trip Itinerary</h5>
                <Button variant="primary" size="sm" className="rounded-pill fw-bold px-3" onClick={() => setShowResModal(true)}>
                    <i className="bi bi-plus-lg me-1"></i> Add Booking
                </Button>
            </div>

            {reservations.length === 0 ? (
                <div className="text-center py-5 text-muted">
                    <i className="bi bi-ticket-detailed display-4 d-block mb-3 opacity-25"></i>
                    <h6>No reservations yet.</h6>
                    <p className="small">Add flights, hotels, and rentals to build your timeline.</p>
                </div>
            ) : (
                <div className="position-relative ms-3 border-start border-2 border-primary border-opacity-25 pb-4">
                    {reservations.map((res) => (
                        <div key={res.id} className="position-relative mb-4 ps-4">
                            <div className="position-absolute bg-primary text-white rounded-circle d-flex align-items-center justify-content-center"
                                 style={{ width: '32px', height: '32px', left: '-17px', top: '0', border: '4px solid white' }}>
                                <i className={`bi ${getResIcon(res.res_type)} small`}></i>
                            </div>
                            <Card className="border-0 shadow-sm bg-light">
                                <Card.Body className="p-3">
                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                        <div>
                                            <Badge bg="secondary" className="mb-1">{res.res_type}</Badge>
                                            <h6 className="fw-bold mb-0 text-primary">{res.provider}</h6>
                                        </div>
                                        <div className="text-end text-nowrap">
                                            {res.confirmation_code && <Badge bg="white" className="text-dark border text-monospace fs-6">{res.confirmation_code}</Badge>}
                                            {/* --- NEW: Edit Button --- */}
                                            <Button variant="link" className="text-primary p-0 ms-3" onClick={() => openEditModal(res)}>
                                                <i className="bi bi-pencil-square hover-scale"></i>
                                            </Button>
                                            <Button variant="link" className="text-danger p-0 ms-2" onClick={() => handleDeleteReservation(res.id)}>
                                                <i className="bi bi-x-circle hover-scale"></i>
                                            </Button>
                                        </div>
                                    </div>
                                    <Row className="small text-muted fw-bold g-2 mt-2">
                                        <Col md={5}>
                                            <div className="text-dark"><i className="bi bi-geo-alt-fill me-1"></i>{res.start_location}</div>
                                            <div>{new Date(res.start_datetime).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</div>
                                        </Col>
                                        <Col md={2} className="text-center d-flex align-items-center justify-content-center opacity-50"><i className="bi bi-arrow-right fs-5"></i></Col>
                                        <Col md={5} className="text-end">
                                            <div className="text-dark">{res.end_location} {res.end_location && <i className="bi bi-geo-alt-fill ms-1"></i>}</div>
                                            <div>{res.end_datetime ? new Date(res.end_datetime).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' }) : '---'}</div>
                                        </Col>
                                    </Row>
                                    {res.notes && <div className="mt-3 pt-2 border-top small text-muted"><i className="bi bi-info-circle me-1"></i> {res.notes}</div>}
                                </Card.Body>
                            </Card>
                        </div>
                    ))}
                </div>
            )}

            {/* --- ADD RESERVATION MODAL --- */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold">Add to Itinerary</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleAddReservation}>
                        <Row className="mb-3 g-2">
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Type</Form.Label>
                                <Form.Select value={newRes.res_type} onChange={e => setNewRes({...newRes, res_type: e.target.value})}>
                                    <option value="Flight">Flight</option><option value="Hotel">Hotel</option>
                                    <option value="Rental Car">Rental Car</option><option value="Train">Train</option><option value="Activity">Activity / Tour</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}><Form.Label className="small fw-bold text-muted mb-1">Provider</Form.Label><Form.Control type="text" value={newRes.provider} onChange={e => setNewRes({...newRes, provider: e.target.value})} required /></Col>
                            <Col md={4}><Form.Label className="small fw-bold text-muted mb-1">Confirmation / PNR</Form.Label><Form.Control type="text" className="text-monospace" value={newRes.confirmation_code} onChange={e => setNewRes({...newRes, confirmation_code: e.target.value})} /></Col>
                        </Row>
                        <Row className="mb-3 g-2">
                            <Col md={6}>
                                <div className="p-3 bg-light rounded border">
                                    <h6 className="small fw-bold text-primary mb-2">{['Hotel', 'Airbnb'].includes(newRes.res_type) ? 'Check-In' : 'Start / Departure'}</h6>
                                    <Form.Control type="datetime-local" className="mb-2" value={newRes.start_datetime} onChange={e => setNewRes({...newRes, start_datetime: e.target.value})} required />
                                    <Form.Control type="text" placeholder="Location" value={newRes.start_location} onChange={e => setNewRes({...newRes, start_location: e.target.value})} />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="p-3 bg-light rounded border">
                                    <h6 className="small fw-bold text-primary mb-2">{['Hotel', 'Airbnb'].includes(newRes.res_type) ? 'Check-Out' : 'End / Arrival'}</h6>
                                    <Form.Control type="datetime-local" className="mb-2" value={newRes.end_datetime} onChange={e => setNewRes({...newRes, end_datetime: e.target.value})} />
                                    <Form.Control type="text" placeholder="Location (Leave blank for Hotel)" value={newRes.end_location} onChange={e => setNewRes({...newRes, end_location: e.target.value})} />
                                </div>
                            </Col>
                        </Row>
                        <Form.Group className="mb-4"><Form.Label className="small fw-bold text-muted mb-1">Notes</Form.Label><Form.Control as="textarea" rows={2} value={newRes.notes} onChange={e => setNewRes({...newRes, notes: e.target.value})} /></Form.Group>
                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">Save to Timeline</Button>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* --- EDIT RESERVATION MODAL --- */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} centered size="lg" fullscreen="md-down">
                <Modal.Header closeButton className="border-0 pb-0"><Modal.Title className="fw-bold">Edit Booking</Modal.Title></Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleUpdateReservation}>
                        <Row className="mb-3 g-2">
                            <Col md={4}>
                                <Form.Label className="small fw-bold text-muted mb-1">Type</Form.Label>
                                <Form.Select value={editRes.res_type} onChange={e => setEditRes({...editRes, res_type: e.target.value})}>
                                    <option value="Flight">Flight</option><option value="Hotel">Hotel</option>
                                    <option value="Rental Car">Rental Car</option><option value="Train">Train</option><option value="Activity">Activity / Tour</option>
                                </Form.Select>
                            </Col>
                            <Col md={4}><Form.Label className="small fw-bold text-muted mb-1">Provider</Form.Label><Form.Control type="text" value={editRes.provider} onChange={e => setEditRes({...editRes, provider: e.target.value})} required /></Col>
                            <Col md={4}><Form.Label className="small fw-bold text-muted mb-1">Confirmation / PNR</Form.Label><Form.Control type="text" className="text-monospace" value={editRes.confirmation_code} onChange={e => setEditRes({...editRes, confirmation_code: e.target.value})} /></Col>
                        </Row>
                        <Row className="mb-3 g-2">
                            <Col md={6}>
                                <div className="p-3 bg-light rounded border">
                                    <h6 className="small fw-bold text-primary mb-2">{['Hotel', 'Airbnb'].includes(editRes.res_type) ? 'Check-In' : 'Start / Departure'}</h6>
                                    <Form.Control type="datetime-local" className="mb-2" value={editRes.start_datetime} onChange={e => setEditRes({...editRes, start_datetime: e.target.value})} required />
                                    <Form.Control type="text" placeholder="Location" value={editRes.start_location} onChange={e => setEditRes({...editRes, start_location: e.target.value})} />
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="p-3 bg-light rounded border">
                                    <h6 className="small fw-bold text-primary mb-2">{['Hotel', 'Airbnb'].includes(editRes.res_type) ? 'Check-Out' : 'End / Arrival'}</h6>
                                    <Form.Control type="datetime-local" className="mb-2" value={editRes.end_datetime} onChange={e => setEditRes({...editRes, end_datetime: e.target.value})} />
                                    <Form.Control type="text" placeholder="Location (Leave blank for Hotel)" value={editRes.end_location} onChange={e => setEditRes({...editRes, end_location: e.target.value})} />
                                </div>
                            </Col>
                        </Row>
                        <Form.Group className="mb-4"><Form.Label className="small fw-bold text-muted mb-1">Notes</Form.Label><Form.Control as="textarea" rows={2} value={editRes.notes} onChange={e => setEditRes({...editRes, notes: e.target.value})} /></Form.Group>
                        <Button variant="primary" type="submit" className="w-100 fw-bold rounded-pill">Save Changes</Button>
                    </Form>
                </Modal.Body>
            </Modal>
        </div>
    );
}
